// ─────────────────────────────────────────────────────────────────────────────
// Live pricing from Chargebee — NEVER hardcode prices.
//
// Prices are fetched at runtime from the fleet billing endpoint (Chargebee-backed,
// same contract used across CloviTek platforms):
//
//     GET /api/billing/plans?platform=manuscriptsmith
//     -> { provider: 'chargebee' | 'degraded', plans: [
//            { id, item_id, plan_name, price_display, price_cents, currency, period }
//          ] }
//
// If the manuscriptsmith Chargebee item family does not exist yet, the endpoint
// returns provider:'degraded' (or 404 / network error), and this module resolves
// to a `degraded` state. Consumers MUST then render a graceful placeholder
// ("Pricing coming soon" / "Contact us") — NEVER a fabricated price.
//
// ⚠️ ACTION REQUIRED (backend): the manuscriptsmith Chargebee item family / price
//    points (manuscriptsmith-free-monthly, manuscriptsmith-author-monthly,
//    manuscriptsmith-pro-monthly) must be created in Chargebee and exposed via
//    /api/billing/plans before real prices will appear here. Until then the UI
//    degrades gracefully by design. Do NOT paper over this with static prices.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';

const PLATFORM = 'manuscriptsmith';
// Allow override via build-time env, else same-origin relative path.
const BILLING_ENDPOINT =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_BILLING_ENDPOINT) ||
  '/api/billing/plans';

/**
 * Normalise the billing response into a map keyed by lowercased plan name,
 * e.g. { free: {...}, author: {...}, pro: {...} }.
 */
function indexPlans(plans) {
  const byName = {};
  (plans || []).forEach((p) => {
    const key = (p.plan_name || p.name || '').toString().toLowerCase().trim();
    if (key) byName[key] = p;
    // also index by the canonical item id (manuscriptsmith-author-monthly -> author)
    const idMatch = (p.id || p.item_id || '').toString().match(/manuscriptsmith-([a-z0-9]+)-/i);
    if (idMatch) byName[idMatch[1].toLowerCase()] = p;
  });
  return byName;
}

/**
 * React hook: live Chargebee pricing.
 * Returns { status: 'loading' | 'ready' | 'degraded', plans: {byName}, provider }.
 * `plans` is only populated when status === 'ready'.
 */
export function useLivePricing() {
  const [state, setState] = useState({ status: 'loading', plans: {}, provider: null });

  useEffect(() => {
    let cancelled = false;
    const url = `${BILLING_ENDPOINT}?platform=${encodeURIComponent(PLATFORM)}`;

    fetch(url, { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((data) => {
        if (cancelled) return;
        const degraded = !data || data.provider === 'degraded' || !Array.isArray(data.plans) || data.plans.length === 0;
        if (degraded) {
          setState({ status: 'degraded', plans: {}, provider: data && data.provider });
        } else {
          setState({ status: 'ready', plans: indexPlans(data.plans), provider: data.provider || 'chargebee' });
        }
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'degraded', plans: {}, provider: 'unreachable' });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/**
 * Resolve the display price for a plan from live data.
 * Returns the Chargebee price_display when available; otherwise `null` so the
 * caller can render a placeholder instead of a made-up number.
 *
 * `isFree` plans legitimately show $0 (that is a fact about a free tier, not an
 * invented price), so callers may pass fallbackFree for those.
 */
export function livePrice(state, planName, { isFree = false } = {}) {
  if (isFree) return '$0';
  if (state.status !== 'ready') return null;
  const p = state.plans[planName.toLowerCase()];
  if (!p) return null;
  return p.price_display || (typeof p.price_cents === 'number' ? `$${(p.price_cents / 100).toFixed(0)}` : null);
}
